// components/consumer/AccountMenu.tsx
"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"

export type AccountMenuAccount = { name: string | null }

type Props = {
  /** null = sin sesión. Solo el nombre: el teléfono jamás sube al cliente. */
  account: AccountMenuAccount | null
  /** Sobre el hero oscuro del marketplace los textos van en blanco. */
  onDark?: boolean
  /** Solo para tests (y un primer render de escritorio si se quisiera): inicializa el desplegable abierto. */
  defaultOpen?: boolean
}

/**
 * Esquina de la cuenta (spec §4.1). Sin sesión, un "Entrar"; con sesión, la
 * inicial en un círculo y un desplegable con Pedidos / Guardados / Cuenta /
 * Salir. El estado lo decide el servidor (getDinerSession) y llega por props:
 * no parpadea. Salir es un POST a /cuenta/salir (la ruta existente de la v1).
 * Enlazar a Inicio es responsabilidad de la cabecera, no de este menú.
 */
export function AccountMenu({ account, onDark = false, defaultOpen = false }: Props) {
  const t = useTranslations("public.consumerNav")
  const [open, setOpen] = useState(defaultOpen)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const fg = onDark ? "#FFFFFF" : "var(--redy-charcoal)"

  if (!account) {
    return (
      <Link
        href="/cuenta/entrar"
        className="rounded-full px-4 py-2 text-[14px] font-bold"
        style={onDark
          ? { backgroundColor: "rgba(255,255,255,0.15)", color: "#FFFFFF", backdropFilter: "blur(4px)" }
          : { backgroundColor: "var(--redy-charcoal)", color: "#FFFFFF" }}
      >
        {t("signIn")}
      </Link>
    )
  }

  const label = account.name?.trim() || t("anonymous")
  const initial = label.charAt(0).toUpperCase()

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("menuLabel")}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-[14px] font-bold"
        style={{ color: fg, backgroundColor: onDark ? "rgba(255,255,255,0.15)" : "var(--redy-surface)", border: onDark ? "none" : "1px solid var(--redy-border)" }}
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-[14px] font-extrabold text-white"
          style={{ backgroundColor: "var(--redy-red)" }}
          aria-hidden="true"
        >
          {initial}
        </span>
        <span className="hidden sm:inline">{label}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-56 overflow-hidden rounded-2xl border shadow-lg"
          style={{ backgroundColor: "var(--redy-surface)", borderColor: "var(--redy-border)" }}
        >
          <p className="px-4 pt-3 pb-2 text-[12px] font-semibold" style={{ color: "var(--redy-muted)" }}>
            {account.name ? t("greeting", { name: account.name }) : t("anonymous")}
          </p>
          <Link role="menuitem" href="/cuenta" className="block px-4 py-2.5 text-sm font-semibold" style={{ color: "var(--redy-charcoal)" }}>{t("orders")}</Link>
          <Link role="menuitem" href="/cuenta/guardados" className="block px-4 py-2.5 text-sm font-semibold" style={{ color: "var(--redy-charcoal)" }}>{t("saved")}</Link>
          <Link role="menuitem" href="/cuenta/descuentos" className="block px-4 py-2.5 text-sm font-semibold" style={{ color: "var(--redy-charcoal)" }}>{t("discounts")}</Link>
          <Link role="menuitem" href="/cuenta/perfil" className="block px-4 py-2.5 text-sm font-semibold" style={{ color: "var(--redy-charcoal)" }}>{t("account")}</Link>
          <form method="post" action="/cuenta/salir" className="border-t" style={{ borderColor: "var(--redy-border-l)" }}>
            <button role="menuitem" type="submit" className="block w-full px-4 py-2.5 text-left text-sm font-semibold" style={{ color: "var(--redy-secondary)" }}>
              {t("signOut")}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
